import React, { useState, useRef, useEffect, useCallback } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { MoreVertical } from "lucide-react";
import RichTextToolbar from "features/analytics/studio/components/shell/RichTextToolbar";
import { sanitizeHtml } from "shared/utils/safeHtml";

// CSS custom properties work inside <style> tags — fully theme-aware
const MD_CSS = `
  .tbv2-md h1 { font-size: 1.2em; font-weight: 700; margin: 0.4em 0 0.2em; color: var(--text-primary); }
  .tbv2-md h2 { font-size: 1.05em; font-weight: 600; margin: 0.3em 0 0.15em; color: var(--text-primary); }
  .tbv2-md h3 { font-size: 0.95em; font-weight: 600; margin: 0.25em 0 0.1em; color: var(--text-primary); }
  .tbv2-md p { margin: 0.2em 0; }
  .tbv2-md ul, .tbv2-md ol { padding-left: 1.25em; margin: 0.2em 0; }
  .tbv2-md li { margin: 0.1em 0; }
  .tbv2-md table { border-collapse: separate; border-spacing: 0; width: 100%; margin: 0.4em 0; border: 1px solid var(--border-default); border-radius: 6px; overflow: hidden; }
  .tbv2-md tr { height: 32px; }
  .tbv2-md th, .tbv2-md td { padding: 0 12px; text-align: left; vertical-align: middle; }
  .tbv2-md th { height: 32px; font-size: 10.5px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em; white-space: nowrap; color: var(--text-muted); border-bottom: 1px solid var(--border-default); }
  .tbv2-md td { font-size: 12px; color: var(--text-primary); border-bottom: 1px solid var(--border-subtle); }
  .tbv2-md tr:last-child td { border-bottom: none; }
  .tbv2-md code { background: var(--surface-sunken); border-radius: 3px; padding: 1px 4px; font-size: 0.85em; font-family: 'Inter', sans-serif; color: var(--text-primary); }
  .tbv2-md pre { background: var(--surface-sunken); border-radius: 6px; padding: 8px 12px; overflow-x: auto; margin: 0.4em 0; border: 1px solid var(--border-default); }
  .tbv2-md pre code { background: none; padding: 0; }
  .tbv2-md blockquote { border-left: 3px solid var(--border-default); margin: 0.4em 0; padding-left: 0.75em; color: var(--text-muted); }
  .tbv2-md a { color: var(--brand-primary); text-decoration: underline; }
  .tbv2-md strong { font-weight: 600; }
`;

const EDITOR_CLASS = "tbv2-rich-editor";

// Shared mini context-menu — used by both variant and regular text cards
function MiniMenu({ menuRef, menuOpen, setMenuOpen, onDelete }) {
  return (
    <div ref={menuRef} className="absolute right-2 top-2 z-10">
      <button
        type="button"
        onClick={() => setMenuOpen((o) => !o)}
        className="p-1 rounded-md text-[color:var(--text-muted)] opacity-0 group-hover/text:opacity-60 hover:!opacity-100 hover:bg-surface-hover transition-all"
      >
        <MoreVertical className="w-4 h-4" />
      </button>
      {menuOpen && (
        <div className="absolute right-0 top-full mt-1 overflow-hidden rounded-md border border-[color:var(--border-default)] bg-surface py-1 shadow-token-lg z-20 min-w-[120px]">
          <button
            type="button"
            onClick={() => { setMenuOpen(false); onDelete(); }}
            className="w-full px-3 py-2 text-left text-[12px] text-[color:var(--feedback-error)] transition-colors hover:bg-[color:color-mix(in_srgb,var(--feedback-error)_6%,transparent)]"
            style={{ fontFamily: "Inter, sans-serif" }}
          >
            Delete
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Text card for the board v2. All colors via CSS design tokens.
 */
export default function TextBoardCard({ card, onSaveContent, onDelete, readOnly }) {
  const q = card?.queryDsl && typeof card.queryDsl === "object" ? card.queryDsl : {};
  const savedContent = q.content ?? "";
  const isMarkdown = q.contentFormat === "markdown";
  const variant = q.textStyle === "heading" || q.textStyle === "divider" || q.textStyle === "kpi"
    ? q.textStyle
    : null;
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const bodyRef = useRef(null);
  const saveTimerRef = useRef(null);
  const blurTimerRef = useRef(null);
  const savedRangeRef = useRef(null);
  const [showToolbar, setShowToolbar] = useState(false);
  const [selectionRect, setSelectionRect] = useState(null);

  useEffect(() => {
    if (!menuOpen) return;
    const close = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [menuOpen]);

  useEffect(() => {
    if (bodyRef.current && savedContent !== undefined && !isMarkdown) {
      const safe = sanitizeHtml(savedContent);
      if (bodyRef.current.innerHTML !== safe) {
        bodyRef.current.innerHTML = safe;
      }
    }
  }, [savedContent, isMarkdown]);

  const persist = useCallback(() => {
    if (!onSaveContent) return;
    const html = bodyRef.current?.innerHTML ?? savedContent;
    onSaveContent({ content: html || "", textStyle: q.textStyle || "text" });
  }, [onSaveContent, savedContent, q.textStyle]);

  const schedulePersist = useCallback(() => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(persist, 1000);
  }, [persist]);

  const checkSelection = useCallback(() => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || !bodyRef.current) return;
    const range = sel.getRangeAt(0);
    if (!bodyRef.current.contains(sel.anchorNode)) return;
    if (range.collapsed) { setShowToolbar(false); setSelectionRect(null); savedRangeRef.current = null; return; }
    try {
      savedRangeRef.current = range.cloneRange();
      const rect = range.getBoundingClientRect();
      if (rect.width > 0) { setSelectionRect({ top: rect.top, left: rect.left, bottom: rect.bottom, width: rect.width, height: rect.height }); setShowToolbar(true); }
    } catch { setShowToolbar(false); }
  }, []);

  const restoreSelection = useCallback(() => {
    const range = savedRangeRef.current;
    if (!range || !bodyRef.current) return;
    try {
      bodyRef.current.focus();
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
    } catch {}
  }, []);

  const handleBlur = () => {
    if (blurTimerRef.current) clearTimeout(blurTimerRef.current);
    blurTimerRef.current = setTimeout(() => {
      setShowToolbar(false);
      setSelectionRect(null);
      savedRangeRef.current = null;
      persist();
      if (saveTimerRef.current) { clearTimeout(saveTimerRef.current); saveTimerRef.current = null; }
    }, 150);
  };

  const cancelBlur = useCallback(() => {
    if (blurTimerRef.current) { clearTimeout(blurTimerRef.current); blurTimerRef.current = null; }
  }, []);

  // ── Report-style variants ─────────────────────────────────────────────
  if (variant) {
    const titleText = card?.title || "";
    return (
      <div className="group/text relative w-full h-full flex items-center min-h-0 overflow-hidden"
           style={{ fontFamily: "Inter, sans-serif" }}>
        {/* Drag handle */}
        {!readOnly && (
          <div
            className="board-drag-handle absolute left-1 top-1/2 -translate-y-1/2 flex items-center justify-center opacity-0 group-hover/text:opacity-60 transition-opacity cursor-grab active:cursor-grabbing z-10 select-none text-[color:var(--text-muted)]"
            style={{ width: 16 }}
          >
            <svg width="10" height="14" viewBox="0 0 10 14" fill="currentColor">
              <circle cx="2.5" cy="2.5" r="1.5" /><circle cx="7.5" cy="2.5" r="1.5" />
              <circle cx="2.5" cy="7"   r="1.5" /><circle cx="7.5" cy="7"   r="1.5" />
              <circle cx="2.5" cy="11.5" r="1.5" /><circle cx="7.5" cy="11.5" r="1.5" />
            </svg>
          </div>
        )}

        {!readOnly && onDelete && (
          <MiniMenu menuRef={menuRef} menuOpen={menuOpen} setMenuOpen={setMenuOpen} onDelete={onDelete} />
        )}

        {variant === "heading" && (
          <div className="w-full px-4 flex items-center">
            <h2
              className="m-0 truncate text-[color:var(--text-primary)]"
              style={{ fontSize: 18, fontWeight: 600, letterSpacing: "-0.01em" }}
            >
              {titleText || "Section"}
            </h2>
          </div>
        )}

        {variant === "divider" && (
          <div className="w-full px-2 flex items-center">
            <div className="w-full" style={{ height: 1, backgroundColor: "var(--border-default)" }} />
          </div>
        )}

        {variant === "kpi" && (() => {
          const valueText = String(savedContent || "—")
            .replace(/^#+\s*/, "")
            .split("\n")[0]
            .trim() || "—";
          return (
            <div className="w-full h-full px-4 py-3 flex flex-col justify-center">
              <div className="text-[11px] uppercase tracking-wide truncate text-[color:var(--text-muted)]" style={{ fontWeight: 600 }}>
                {titleText}
              </div>
              <div
                className="truncate text-[color:var(--text-primary)]"
                style={{
                  fontSize: 28,
                  fontWeight: 700,
                  lineHeight: 1.1,
                  marginTop: 6,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {valueText}
              </div>
            </div>
          );
        })()}
      </div>
    );
  }

  return (
    <div className="group/text relative w-full h-full flex flex-col min-h-0 overflow-hidden">
      <style>{MD_CSS}</style>
      <style>{`.${EDITOR_CLASS} p,.${EDITOR_CLASS} div{margin:0}.${EDITOR_CLASS} ul,.${EDITOR_CLASS} ol{margin:.25em 0;padding-left:1.25em}.${EDITOR_CLASS} ul{list-style-type:disc}.${EDITOR_CLASS} ol{list-style-type:decimal}.${EDITOR_CLASS} li{margin:.1em 0}`}</style>

      {/* Drag handle */}
      {!readOnly && (
        <div
          className="board-drag-handle absolute left-1 top-2 flex items-center justify-center opacity-0 group-hover/text:opacity-60 transition-opacity cursor-grab active:cursor-grabbing z-10 select-none text-[color:var(--text-muted)]"
          style={{ width: 16 }}
        >
          <svg width="10" height="14" viewBox="0 0 10 14" fill="currentColor">
            <circle cx="2.5" cy="2.5" r="1.5" /><circle cx="7.5" cy="2.5" r="1.5" />
            <circle cx="2.5" cy="7"   r="1.5" /><circle cx="7.5" cy="7"   r="1.5" />
            <circle cx="2.5" cy="11.5" r="1.5" /><circle cx="7.5" cy="11.5" r="1.5" />
          </svg>
        </div>
      )}

      {/* ⋮ menu */}
      {!readOnly && onDelete && (
        <MiniMenu menuRef={menuRef} menuOpen={menuOpen} setMenuOpen={setMenuOpen} onDelete={onDelete} />
      )}

      {/* Content area */}
      <div className="flex-1 min-h-0 overflow-auto p-3" style={{ paddingLeft: readOnly ? 12 : 20 }}>
        {isMarkdown ? (
          <div
            className="tbv2-md"
            style={{ fontSize: 13, color: "var(--text-secondary)", fontFamily: "Inter, sans-serif", lineHeight: 1.6 }}
          >
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{savedContent}</ReactMarkdown>
          </div>
        ) : !readOnly && onSaveContent ? (
          <>
            <RichTextToolbar
              editorRef={bodyRef}
              visible={showToolbar}
              selectionRect={selectionRect}
              restoreSelection={restoreSelection}
              onAfterAction={() => { setShowToolbar(false); setSelectionRect(null); schedulePersist(); }}
              onToolbarInteraction={cancelBlur}
              currentTextStyle={q.textStyle || "text"}
            />
            <div
              ref={bodyRef}
              contentEditable
              suppressContentEditableWarning
              onInput={schedulePersist}
              onMouseUp={() => setTimeout(checkSelection, 0)}
              onKeyUp={() => setTimeout(checkSelection, 0)}
              onBlur={handleBlur}
              data-placeholder="Write your text..."
              className={`${EDITOR_CLASS} w-full outline-none leading-relaxed empty:before:content-[attr(data-placeholder)] empty:before:text-[color:var(--text-muted)]`}
              style={{ fontSize: 13, color: "var(--text-secondary)", fontFamily: "Inter, sans-serif", minHeight: "2rem" }}
            />
          </>
        ) : (
          <div
            className={EDITOR_CLASS}
            style={{ fontSize: 13, color: "var(--text-secondary)", fontFamily: "Inter, sans-serif", lineHeight: 1.6 }}
            // A teammate's HTML (and, on a public share, shown to anyone): never raw.
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(savedContent) }}
          />
        )}
      </div>
    </div>
  );
}
